/**
 * ContactSubmissionsSection — Bandeja de leads del formulario "Contáctanos"
 * de la landing pública. Solo visible por Super Admin (v1.4.5).
 *
 * Antes vivía en InstitutionAdminDashboard, pero los leads son responsabilidad
 * de ventas/onboarding, no de directores de escuela específicos.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function ContactSubmissionsSection() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/contact-submissions`, { withCredentials: true });
      setSubmissions(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const mark = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contact-submissions/${id}`, { status }, { withCredentials: true });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  useEffect(() => { load(); }, []);

  const statusPill = (s) => {
    const map = {
      pending: { label: "Pendiente", cls: "bg-yellow-50 text-yellow-800" },
      reviewed: { label: "Revisado", cls: "bg-blue-50 text-blue-700" },
      contacted: { label: "Contactado", cls: "bg-green-50 text-green-700" },
    };
    const cfg = map[s] || map.pending;
    return <Badge className={cfg.cls}>{cfg.label}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="contact-submissions-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Solicitudes de Contacto</h2>
          <p className="text-xs text-gray-500 mt-1">
            Leads recibidos desde el formulario "Contáctanos" de la landing pública.
            La columna <b>Institución</b> corresponde a la institución del remitente,
            no de tu cuenta.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="refresh-contacts-btn"
        >
          {loading ? "Cargando..." : "Actualizar"}
        </Button>
      </div>

      {submissions.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center" data-testid="no-contacts">
          No hay solicitudes aún.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Fecha</TableHead>
                <TableHead>Institución</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mensaje</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="sticky right-0 bg-white text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map((s) => (
                <TableRow key={s.id} data-testid={`contact-row-${s.id}`}>
                  <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString("es") : "-"}
                  </TableCell>
                  <TableCell className="font-medium">{s.institution}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell><Badge className="bg-gray-100 text-gray-700">{s.role}</Badge></TableCell>
                  <TableCell className="text-sm">{s.email}</TableCell>
                  <TableCell className="max-w-[240px] truncate text-sm text-gray-600" title={s.message}>
                    {s.message}
                  </TableCell>
                  <TableCell>{statusPill(s.status)}</TableCell>
                  <TableCell className="sticky right-0 bg-white text-right">
                    {s.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => mark(s.id, "reviewed")}
                        data-testid={`mark-reviewed-${s.id}`}
                      >
                        Marcar revisado
                      </Button>
                    )}
                    {s.status === "reviewed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => mark(s.id, "contacted")}
                        data-testid={`mark-contacted-${s.id}`}
                      >
                        Contactado
                      </Button>
                    )}
                    {s.status === "contacted" && (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
